/**
 * Centralized Auth Context
 * P1 Fix: Caches user data to prevent duplicate supabase.auth.getUser() calls
 * across the application. All components should use this context instead of
 * calling supabase.auth.getUser() directly.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { structuredLogger } from "@/lib/logging";
import { clearQuizDrafts, readLastAuthUserId, writeLastAuthUserId } from "@/lib/quizDrafts";
import { clearAllLastModules } from "@/lib/lastModule";
import type { User, Session } from "@supabase/supabase-js";


interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** An unresolved or failed SDK/session read is not a signed-out result. */
  initializationFailed: boolean;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  isAuthenticated: false,
  initializationFailed: false,
  refetch: async () => {},
});

const isRealUserSession = (session: Session | null): session is Session =>
  !!session?.user && !(session.user as User & { is_anonymous?: boolean }).is_anonymous;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initializationFailed, setInitializationFailed] = useState(false);
  const mounted = useRef(false);
  const revision = useRef(0);
  const pendingTimers = useRef(new Set<number>());
  /** Last known signed-in user id, used to detect sign-out / account switches. */
  const lastUserIdRef = useRef<string | null>(null);

  /**
   * Purge device-local learner drafts whenever the active account changes —
   * including token refresh, session restore on boot, and account switches
   * made in another tab. The previous user id is also persisted to
   * localStorage so a change is detected even when this tab starts cold.
   */
  const syncLocalDraftsForUser = useCallback((nextUserId: string | null) => {
    const previous = lastUserIdRef.current ?? readLastAuthUserId();
    lastUserIdRef.current = nextUserId;
    writeLastAuthUserId(nextUserId);
    if (previous === nextUserId) return;
    if (nextUserId) {
      clearQuizDrafts(nextUserId);
      clearAllLastModules(nextUserId);
    } else {
      clearQuizDrafts();
      clearAllLastModules();
    }
  }, []);



  const fetchUser = useCallback(async () => {
    const attempt = ++revision.current;
    let stage: "sdk-initialization" | "session-read" = "sdk-initialization";
    const current = () => mounted.current && attempt === revision.current;
    const fail = (outcome: "timeout" | "returned-error" | "rejected") => {
      if (!current()) return;
      ++revision.current; // Late results cannot replace a failed or newer attempt.
      setInitializationFailed(true);
      // Keep loading/access closed and preserve session/drafts. Unknown is not signed out.
      structuredLogger.warn("Auth initialization failed", { stage, outcome });
    };
    setIsLoading(true);
    setInitializationFailed(false);
    const timer = window.setTimeout(() => {
      pendingTimers.current.delete(timer);
      fail("timeout");
    }, 12_000);
    pendingTimers.current.add(timer);
    try {
      // Public SDK API: initialization and session-read failures are distinguished
      // without inspecting tokens, storage, lock names or private SDK state.
      const initialized = await supabase.auth.initialize();
      if (!current()) return;
      if (initialized.error) { fail("returned-error"); return; }
      stage = "session-read";
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();
      if (!current()) return;
      if (error) { fail("returned-error"); return; }
      if (isRealUserSession(currentSession)) {
        syncLocalDraftsForUser(currentSession.user.id);
        setSession(currentSession);
        setUser(currentSession.user);
      } else {
        syncLocalDraftsForUser(null);
        setSession(null);
        setUser(null);
      }
      setIsLoading(false);
    } catch {
      fail("rejected");
    } finally {
      window.clearTimeout(timer);
      pendingTimers.current.delete(timer);
    }
  }, [syncLocalDraftsForUser]);

  useEffect(() => {
    mounted.current = true;
    void fetchUser();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // The SDK also emits INITIAL_SESSION(null) when its read fails. Let the
        // error-aware fetch above settle initialization instead of purging drafts.
        if (event === "INITIAL_SESSION") return;
        if (!mounted.current) return;
        if (!newSession && event !== "SIGNED_OUT") return;
        ++revision.current; // Auth events supersede earlier reads/refetches.
        if (isRealUserSession(newSession)) {
          syncLocalDraftsForUser(newSession.user.id);
          setSession(newSession);
          setUser(newSession.user);
        } else {
          syncLocalDraftsForUser(null);
          setSession(null);
          setUser(null);
        }
        setInitializationFailed(false);
        setIsLoading(false);
      }
    );
    return () => {
      mounted.current = false;
      ++revision.current;
      subscription.unsubscribe();
      pendingTimers.current.forEach((timer) => window.clearTimeout(timer));
      pendingTimers.current.clear();
    };
  }, [fetchUser, syncLocalDraftsForUser]);


  // Run outside onAuthStateChange to avoid holding Supabase's Auth callback lock.
  // Restored sessions retry failed delivery; server eligibility/log/idempotency are authoritative.
  useEffect(() => {
    if (!user?.id || !user.email_confirmed_at) return;
    void supabase.functions.invoke("create-account", { body: { action: "verified-welcome" } })
      .then(({ error }) => {
        if (error) structuredLogger.warn("Verified welcome deferred");
      }).catch(() => structuredLogger.warn("Verified welcome deferred"));
  }, [user?.id, user?.email_confirmed_at]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    session,
    isLoading,
    initializationFailed,
    isAuthenticated: !!user && !(user as User & { is_anonymous?: boolean }).is_anonymous,
    refetch: fetchUser,
  }), [user, session, isLoading, initializationFailed, fetchUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

/**
 * Hook to get current user ID without triggering new fetch
 * Returns null if not authenticated
 */
export const useUserId = (): string | null => {
  const { user } = useAuth();
  return user?.id ?? null;
};

